import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getRandomSoalSinonim, getRandomSoalAntonim, getRandomSoalKataGanjil, soalSinonim, soalAntonim, soalKataGanjil } from './bankSoalVerbal';
import { catatHasilTes } from './progressTracker';
import { useCegahKeluarTes } from './cegahKeluarTes';
import { bacaProgresTes, hapusProgresTes, useAutosaveTes } from './useResumeTes';

const SEMUA_BANK_VERBAL = [...soalSinonim, ...soalAntonim, ...soalKataGanjil];
function soalDariMetaVerbal(meta) {
  const byId = {};
  SEMUA_BANK_VERBAL.forEach(s => { byId[s.id] = s; });
  return (meta || []).map(m => (byId[m.id] ? { ...byId[m.id], jenis: m.jenis } : null)).filter(Boolean);
}

const TOTAL_SOAL  = 30;
const WAKTU_TOTAL = 25 * 60;

function formatWaktu(detik) {
  const m = Math.floor(detik / 60).toString().padStart(2, '0');
  const s = (detik % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function hitungSkorPer(soalList, jawabanUser) {
  let benar = 0, salah = 0, kosong = 0;
  soalList.forEach(soal => {
    const jwb = jawabanUser.find(j => j.id === soal.id)?.jawaban;
    if (!jwb) kosong++;
    else if (jwb === soal.jawaban) benar++; else salah++;
  });
  return { benar, salah, kosong, skor: Math.round((benar / (soalList.length || 1)) * 100), total: soalList.length };
}

function getBadge(skor) {
  if (skor >= 90) return { label: 'Luar Biasa', color: 'text-green-700 bg-green-100 border-green-300' };
  if (skor >= 75) return { label: 'Baik', color: 'text-blue-700 bg-blue-100 border-blue-300' };
  if (skor >= 60) return { label: 'Cukup', color: 'text-yellow-700 bg-yellow-100 border-yellow-300' };
  return { label: 'Perlu Latihan', color: 'text-red-700 bg-red-100 border-red-300' };
}

function getLabelJenis(jenis) {
  if (jenis === 'sinonim') return { label: 'Sinonim', icon: '🔵', color: 'bg-blue-100 text-blue-700' };
  if (jenis === 'antonim') return { label: 'Antonim', icon: '🔴', color: 'bg-red-100 text-red-700' };
  return { label: 'Kata Ganjil', icon: '🟡', color: 'bg-yellow-100 text-yellow-700' };
}

function BannerLanjutkanVerbal({ progresTersimpan, onLanjutkan, onMulaiBaru }) {
  if (!progresTersimpan) return null;
  const labelMode = progresTersimpan.mode === 'sinonim' ? 'Sinonim'
    : progresTersimpan.mode === 'antonim' ? 'Antonim'
    : progresTersimpan.mode === 'kata_ganjil' ? 'Kata Ganjil'
    : 'Campuran';
  const totalSoal = progresTersimpan.soalMeta?.length || 0;
  return (
    <div className="rounded-xl p-4 mb-5 border-2 border-purple-400 bg-purple-50">
      <p className="text-xs font-bold uppercase tracking-wide text-purple-700 mb-1">⏸️ Ada Pengerjaan Sebelumnya</p>
      <p className="text-sm text-purple-800 mb-3">
        Mode <b>{labelMode}</b>, baru sampai soal <b>{(progresTersimpan.indeks || 0) + 1}/{totalSoal}</b>, sisa waktu <b>{formatWaktu(progresTersimpan.waktuSisa || 0)}</b>.
      </p>
      <div className="flex gap-2">
        <button onClick={onLanjutkan} className="flex-1 py-2 rounded-lg bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 transition">
          Lanjutkan
        </button>
        <button onClick={onMulaiBaru} className="flex-1 py-2 rounded-lg border border-purple-300 text-purple-600 text-sm font-semibold hover:bg-purple-100 transition">
          Mulai Baru
        </button>
      </div>
    </div>
  );
}

// ─── Layar Intro ─────────────────────────────────────────────
function LayarIntro({ onMulai, onBack, tryoutMode, progresTersimpan, onLanjutkan, onMulaiBaru }) {
  const [mode, setMode] = useState(null);

  const MODES = [
    {
      id: 'sinonim',
      icon: '🔵',
      label: 'Sinonim',
      desc: '30 soal — temukan kata bermakna serupa',
      detail: 'Fokus latihan sinonim saja, tidak tercampur tipe lain',
      border: 'border-blue-400', bg: 'bg-blue-50',
      iconBg: 'bg-blue-100', textColor: 'text-blue-700', checkColor: 'text-blue-600',
    },
    {
      id: 'antonim',
      icon: '🔴',
      label: 'Antonim',
      desc: '30 soal — temukan kata bermakna berlawanan',
      detail: 'Fokus latihan antonim saja, tidak tercampur tipe lain',
      border: 'border-red-400', bg: 'bg-red-50',
      iconBg: 'bg-red-100', textColor: 'text-red-700', checkColor: 'text-red-600',
    },
    {
      id: 'kata_ganjil',
      icon: '🟡',
      label: 'Kata Ganjil (VKT-2)',
      desc: '30 soal — temukan kata yang tidak sekelompok',
      detail: 'Latihan fokus pengelompokan konsep kata',
      border: 'border-yellow-400', bg: 'bg-yellow-50',
      iconBg: 'bg-yellow-100', textColor: 'text-yellow-700', checkColor: 'text-yellow-600',
    },
    {
      id: 'campuran',
      icon: '📚',
      label: 'Campuran',
      desc: '10 sinonim + 10 antonim + 10 kata ganjil',
      detail: 'Skor tiap kategori ditampilkan terpisah',
      border: 'border-purple-400', bg: 'bg-purple-50',
      iconBg: 'bg-purple-100', textColor: 'text-purple-700', checkColor: 'text-purple-600',
    },
  ];

  const selected = MODES.find(m => m.id === mode);
  const modeCampuran = MODES.find(m => m.id === 'campuran');

  // Dalam konteks Tryout resmi, mode dikunci ke Campuran — user tidak boleh
  // memilih submode sendiri (supaya semua peserta mengerjakan komposisi soal
  // yang sama/adil). Tampilkan notice besar biar jelas kenapa tidak ada pilihan.
  if (tryoutMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
          <div className="text-center mb-5">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl">📚</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Tes Verbal</h1>
            <p className="text-gray-400 text-sm mt-1">Bagian dari Tryout Resmi</p>
          </div>

          <BannerLanjutkanVerbal progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

          <div className="rounded-xl p-4 mb-4 border-2 border-amber-400 bg-amber-50">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-1">📌 Perhatian</p>
            <p className="text-sm font-semibold text-amber-800 leading-relaxed">
              Karena ini bagian dari Tryout resmi, mode dikunci otomatis ke <u>Campuran</u> — semua peserta mengerjakan komposisi soal yang sama, tidak bisa memilih mode lain.
            </p>
          </div>

          <div className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 ${modeCampuran.border} ${modeCampuran.bg} mb-5`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${modeCampuran.iconBg}`}>
              <span className="text-2xl">{modeCampuran.icon}</span>
            </div>
            <div className="flex-1">
              <p className={`font-bold text-sm ${modeCampuran.textColor}`}>{modeCampuran.label}</p>
              <p className="text-xs text-gray-500">{modeCampuran.desc}</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
            <div><p className="text-lg font-bold text-gray-800">30</p><p className="text-xs text-gray-500">Soal</p></div>
            <div><p className="text-lg font-bold text-gray-800">25'</p><p className="text-xs text-gray-500">Waktu</p></div>
            <div><p className="text-lg font-bold text-gray-800">~50"</p><p className="text-xs text-gray-500">Per soal</p></div>
          </div>

          <button onClick={() => onMulai('campuran')}
            className="w-full py-3 rounded-xl font-bold border-2 border-purple-400 bg-purple-50 text-purple-700 hover:opacity-90 transition">
            Mulai Sekarang →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">📚</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Tes Verbal</h1>
          <p className="text-gray-400 text-sm mt-1">Pilih mode latihan</p>
        </div>

        <BannerLanjutkanVerbal progresTersimpan={progresTersimpan} onLanjutkan={onLanjutkan} onMulaiBaru={onMulaiBaru} />

        <div className="space-y-3 mb-5">
          {MODES.map(m => (
            <button key={m.id} onClick={() => setMode(m.id)}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition text-left
                ${mode === m.id ? `${m.border} ${m.bg}` : 'border-gray-200 bg-white hover:border-gray-300'}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                ${mode === m.id ? m.iconBg : 'bg-gray-100'}`}>
                <span className="text-2xl">{m.icon}</span>
              </div>
              <div className="flex-1">
                <p className={`font-bold text-sm ${mode === m.id ? m.textColor : 'text-gray-700'}`}>{m.label}</p>
                <p className="text-xs text-gray-500">{m.desc}</p>
                {mode === m.id && <p className={`text-xs mt-1 font-medium ${m.textColor}`}>✦ {m.detail}</p>}
              </div>
              {mode === m.id && <span className={`text-xl font-bold ${m.checkColor}`}>✓</span>}
            </button>
          ))}
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-5 grid grid-cols-3 gap-3 text-center">
          <div><p className="text-lg font-bold text-gray-800">30</p><p className="text-xs text-gray-500">Soal</p></div>
          <div><p className="text-lg font-bold text-gray-800">25'</p><p className="text-xs text-gray-500">Waktu</p></div>
          <div><p className="text-lg font-bold text-gray-800">~50"</p><p className="text-xs text-gray-500">Per soal</p></div>
        </div>

        <div className="flex gap-3">
          <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">← Kembali</button>
          <button onClick={() => mode && onMulai(mode)} disabled={!mode}
            className={`flex-grow py-3 rounded-xl font-bold transition
              ${mode ? `${selected?.border} border-2 ${selected?.bg} ${selected?.textColor} hover:opacity-90` : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
            {mode ? `Mulai ${selected?.label} →` : 'Pilih mode dulu'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Soal Verbal (semua tipe pilihan ganda) ───────────────────
function SoalVerbal({ soal, onJawab, jawaban }) {
  const huruf = ['a', 'b', 'c', 'd', 'e'];
  const info = getLabelJenis(soal.jenis);

  const bgSoal = soal.jenis === 'sinonim' ? 'bg-blue-50 border-blue-100'
    : soal.jenis === 'antonim' ? 'bg-red-50 border-red-100'
    : 'bg-yellow-50 border-yellow-100';

  const borderPilihan = soal.jenis === 'sinonim' ? 'border-blue-500 bg-blue-100 text-blue-800'
    : soal.jenis === 'antonim' ? 'border-red-500 bg-red-100 text-red-800'
    : 'border-yellow-500 bg-yellow-100 text-yellow-800';

  const hoverPilihan = soal.jenis === 'sinonim' ? 'hover:border-blue-300 hover:bg-blue-50'
    : soal.jenis === 'antonim' ? 'hover:border-red-300 hover:bg-red-50'
    : 'hover:border-yellow-300 hover:bg-yellow-50';

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide ${info.color}`}>
          {info.icon} {info.label}
        </span>
        <span className="text-xs text-gray-400">
          {soal.jenis === 'kata_ganjil' ? 'Temukan yang tidak sekelompok' : `Pilih ${info.label.toLowerCase()} yang tepat`}
        </span>
      </div>
      <div className={`border rounded-xl p-5 mb-4 text-center ${bgSoal}`}>
        <p className="text-xl font-bold text-gray-800 tracking-wider">{soal.soal}</p>
      </div>
      <div className="space-y-2">
        {huruf.map(h => (
          (h in soal.pilihan) && (
            <button key={h} onClick={() => onJawab(h)}
              className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium transition text-sm
                ${jawaban === h ? borderPilihan : `border-gray-200 bg-white text-gray-700 ${hoverPilihan}`}`}>
              <span className="font-bold mr-2 uppercase">{h}.</span>{soal.pilihan[h]}
            </button>
          )
        ))}
      </div>
    </div>
  );
}

// ─── Kartu Skor ───────────────────────────────────────────────
function KartuSkor({ label, icon, hasil, borderColor }) {
  const badge = getBadge(hasil.skor);
  return (
    <div className={`rounded-xl p-4 border-2 ${borderColor}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">{icon} {label}</p>
          <p className="text-3xl font-extrabold text-gray-800 mt-1">{hasil.skor}</p>
          <p className="text-xs text-gray-400">dari 100 poin</p>
        </div>
        <span className={`text-xs font-bold px-3 py-1 rounded-full border ${badge.color}`}>{badge.label}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div className="h-2 rounded-full bg-purple-500 transition-all duration-1000" style={{ width: `${hasil.skor}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="bg-green-50 rounded-lg py-2"><p className="font-bold text-green-700 text-base">{hasil.benar}</p><p className="text-gray-500">Benar</p></div>
        <div className="bg-red-50 rounded-lg py-2"><p className="font-bold text-red-700 text-base">{hasil.salah}</p><p className="text-gray-500">Salah</p></div>
        <div className="bg-gray-50 rounded-lg py-2"><p className="font-bold text-gray-600 text-base">{hasil.kosong}</p><p className="text-gray-500">Kosong</p></div>
      </div>
    </div>
  );
}

// ─── Layar Hasil ──────────────────────────────────────────────
function LayarHasil({ mode, soalList, jawabanUser, onUlang, onBack, tryoutMode, tryoutId }) {
  const [showPembahasan, setShowPembahasan] = useState(false);

  const soalSin = soalList.filter(s => s.jenis === 'sinonim');
  const soalAnt = soalList.filter(s => s.jenis === 'antonim');
  const soalVkt = soalList.filter(s => s.jenis === 'kata_ganjil');

  const hasilSin = soalSin.length > 0 ? hitungSkorPer(soalSin, jawabanUser) : null;
  const hasilAnt = soalAnt.length > 0 ? hitungSkorPer(soalAnt, jawabanUser) : null;
  const hasilVkt = soalVkt.length > 0 ? hitungSkorPer(soalVkt, jawabanUser) : null;

  const skorTotal = mode === 'campuran'
    ? Math.round(([hasilSin, hasilAnt, hasilVkt].filter(Boolean).reduce((a, b) => a + b.skor, 0)) / 3)
    : mode === 'sinonim'
      ? hasilSin?.skor || 0
      : mode === 'antonim'
        ? hasilAnt?.skor || 0
        : hasilVkt?.skor || 0;

  const badgeTotal = getBadge(skorTotal);

  // Gabungkan rincian benar/salah/kosong dari ketiga tipe (kalau campuran)
  // atau ambil dari mode yang aktif saja — supaya detail ini bisa dipakai laporan.
  const detailTotal = mode === 'campuran'
    ? [hasilSin, hasilAnt, hasilVkt].filter(Boolean).reduce((acc, h) => ({
        benar: acc.benar + h.benar, salah: acc.salah + h.salah, kosong: acc.kosong + h.kosong, total: acc.total + h.total,
      }), { benar: 0, salah: 0, kosong: 0, total: 0 })
    : mode === 'sinonim' ? hasilSin : mode === 'antonim' ? hasilAnt : hasilVkt;

  // Catat hasil tes ke progress tracker (sekali saat hasil ditampilkan)
  useEffect(() => {
    catatHasilTes('verbal', skorTotal, { mode, benar: detailTotal?.benar, salah: detailTotal?.salah, kosong: detailTotal?.kosong, total: detailTotal?.total }, tryoutMode, tryoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const labelMode = mode === 'sinonim' ? '🔵 Sinonim'
    : mode === 'antonim' ? '🔴 Antonim'
    : mode === 'kata_ganjil' ? '🟡 Kata Ganjil'
    : '📚 Campuran';

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Skor utama */}
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <div className="text-4xl mb-2">{skorTotal >= 75 ? '🏆' : skorTotal >= 60 ? '👍' : '📚'}</div>
          <p className="text-xs text-gray-400 mb-1">Mode: {labelMode}</p>
          <h2 className="text-lg font-bold text-gray-800 mb-3">Hasil Tes Verbal</h2>
          <div className="text-6xl font-extrabold text-purple-600 mb-1">{skorTotal}</div>
          <p className="text-gray-400 text-sm mb-3">Skor Total</p>
          <span className={`inline-block px-4 py-1 rounded-full text-sm font-bold border ${badgeTotal.color}`}>{badgeTotal.label}</span>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-4 mb-2">
            <div className="bg-purple-500 h-3 rounded-full transition-all duration-1000" style={{ width: `${skorTotal}%` }} />
          </div>
          <p className="text-xs text-gray-400">💡 Skor ini akan digabungkan ke nilai akhir psikotes kamu</p>
        </div>

        {/* Rincian skor per kategori */}
        {mode === 'sinonim' && hasilSin && (
          <KartuSkor label="Sinonim" icon="🔵" hasil={hasilSin} borderColor="border-blue-200" />
        )}
        {mode === 'antonim' && hasilAnt && (
          <KartuSkor label="Antonim" icon="🔴" hasil={hasilAnt} borderColor="border-red-200" />
        )}
        {mode === 'kata_ganjil' && hasilVkt && (
          <KartuSkor label="Kata Ganjil" icon="🟡" hasil={hasilVkt} borderColor="border-yellow-200" />
        )}
        {mode === 'campuran' && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Rincian Skor per Kategori</p>
            {hasilSin && <KartuSkor label="Sinonim" icon="🔵" hasil={hasilSin} borderColor="border-blue-200" />}
            {hasilAnt && <KartuSkor label="Antonim" icon="🔴" hasil={hasilAnt} borderColor="border-red-200" />}
            {hasilVkt && <KartuSkor label="Kata Ganjil" icon="🟡" hasil={hasilVkt} borderColor="border-yellow-200" />}
          </div>
        )}

        {/* Tombol */}
        <div className="bg-white rounded-2xl shadow p-4 space-y-3">
          <button onClick={() => setShowPembahasan(!showPembahasan)}
            className="w-full py-3 rounded-xl border border-purple-300 text-purple-600 font-semibold hover:bg-purple-50 transition">
            {showPembahasan ? 'Sembunyikan' : '📋 Lihat'} Pembahasan
          </button>
          <div className="flex gap-3">
            <button onClick={onUlang} className="flex-1 py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition">🔄 Ulangi</button>
            <button onClick={onBack} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition">← Menu</button>
          </div>
        </div>

        {/* Pembahasan */}
        {showPembahasan && (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Pembahasan Soal</p>
            {soalList.map((soal, idx) => {
              const jwb = jawabanUser.find(j => j.id === soal.id)?.jawaban;
              const benar = jwb === soal.jawaban;
              const info = getLabelJenis(soal.jenis);
              return (
                <div key={soal.id} className={`bg-white rounded-xl p-4 border-l-4 ${benar ? 'border-green-500' : 'border-red-400'}`}>
                  <div className="flex justify-between items-start mb-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${info.color}`}>{idx + 1}. {info.icon} {info.label}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${benar ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {benar ? '✓ Benar' : '✗ Salah'}
                    </span>
                  </div>
                  <p className="text-sm font-bold text-gray-700 mb-2">{soal.soal}</p>
                  <p className="text-sm">
                    <span className="text-gray-400">Jawabanmu: </span>
                    <span className={`font-bold ${benar ? 'text-green-600' : 'text-red-500'}`}>
                      {jwb ? `${jwb.toUpperCase()}. ${soal.pilihan[jwb]}` : '—'}
                    </span>
                    {!benar && (
                      <span className="ml-2 text-gray-400">
                        | Benar: <span className="font-bold text-green-600">{soal.jawaban.toUpperCase()}. {soal.pilihan[soal.jawaban]}</span>
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 bg-gray-50 rounded-lg px-3 py-2">💡 {soal.pembahasan}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Komponen Utama ───────────────────────────────────────────
export default function VerbalModul({ onBack, tryoutMode = false, tryoutId = null }) {
  const [fase, setFase]           = useState('intro');
  useCegahKeluarTes(fase === 'tes', onBack);
  const [mode, setMode]           = useState(null);
  const [soalList, setSoalList]   = useState([]);
  const [indeks, setIndeks]       = useState(0);
  const [jawabanUser, setJawabanUser] = useState([]);
  const [waktuSisa, setWaktuSisa] = useState(WAKTU_TOTAL);
  const timerRef                  = useRef(null);
  const [progresTersimpan, setProgresTersimpan] = useState(null);

  useEffect(() => {
    setProgresTersimpan(bacaProgresTes('verbal', tryoutId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAutosaveTes(fase === 'tes', 'verbal', tryoutId, {
    mode,
    soalMeta: soalList.map(s => ({ id: s.id, jenis: s.jenis })),
    indeks,
    jawabanUser,
    waktuSisa,
  });

  function generateSoal(selectedMode) {
    if (selectedMode === 'sinonim') {
      return getRandomSoalSinonim(TOTAL_SOAL);
    } else if (selectedMode === 'antonim') {
      return getRandomSoalAntonim(TOTAL_SOAL);
    } else if (selectedMode === 'kata_ganjil') {
      return getRandomSoalKataGanjil(TOTAL_SOAL);
    } else {
      // Mode campuran: kategori TIDAK diacak silang lagi (dulu di-shuffle penuh
      // sehingga 3 kategori bercampur acak dan user gampang bingung). Sekarang
      // tiap kategori dikerjakan berurutan per blok — tetap 1 modul/1 alur soal
      // 1..30, cuma bloknya jelas. Lihat juga divider "Mulai bagian ..." di layar soal.
      const sin = getRandomSoalSinonim(10);
      const ant = getRandomSoalAntonim(10);
      const vkt = getRandomSoalKataGanjil(10);
      return [...sin, ...ant, ...vkt];
    }
  }

  const handleMulai = useCallback((selectedMode) => {
    hapusProgresTes('verbal', tryoutId);
    const soal = generateSoal(selectedMode);
    setMode(selectedMode);
    setSoalList(soal);
    setIndeks(0);
    setJawabanUser(soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(WAKTU_TOTAL);
    setProgresTersimpan(null);
    setFase('tes');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryoutId]);

  const handleLanjutkan = useCallback(() => {
    if (!progresTersimpan) return;
    const soal = soalDariMetaVerbal(progresTersimpan.soalMeta);
    if (soal.length === 0) { setProgresTersimpan(null); return; }
    setMode(progresTersimpan.mode);
    setSoalList(soal);
    setIndeks(Math.min(progresTersimpan.indeks || 0, soal.length - 1));
    setJawabanUser(progresTersimpan.jawabanUser || soal.map(s => ({ id: s.id, jawaban: null })));
    setWaktuSisa(progresTersimpan.waktuSisa ?? WAKTU_TOTAL);
    setFase('tes');
  }, [progresTersimpan]);

  const handleMulaiBaruDariBanner = useCallback(() => {
    hapusProgresTes('verbal', tryoutId);
    setProgresTersimpan(null);
  }, [tryoutId]);

  useEffect(() => {
    if (fase !== 'tes') return;
    timerRef.current = setInterval(() => {
      setWaktuSisa(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); hapusProgresTes('verbal', tryoutId); setFase('hasil'); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fase]);

  const handleJawab = useCallback((nilai) => {
    setJawabanUser(prev => prev.map(j => j.id === soalList[indeks].id ? { ...j, jawaban: nilai } : j));
  }, [indeks, soalList]);

  const handleNext = useCallback(() => {
    if (indeks < soalList.length - 1) setIndeks(i => i + 1);
    else { clearInterval(timerRef.current); hapusProgresTes('verbal', tryoutId); setFase('hasil'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indeks, soalList.length, tryoutId]);

  const handlePrev = useCallback(() => { if (indeks > 0) setIndeks(i => i - 1); }, [indeks]);

  const handleSelesai = useCallback(() => {
    clearInterval(timerRef.current);
    hapusProgresTes('verbal', tryoutId);
    setFase('hasil');
  }, [tryoutId]);

  const handleUlang = useCallback(() => {
    setFase('intro'); setMode(null); setSoalList([]); setIndeks(0);
    setJawabanUser([]); setWaktuSisa(WAKTU_TOTAL);
  }, []);

  if (fase === 'intro') {
    return (
      <LayarIntro
        onMulai={handleMulai}
        onBack={onBack}
        tryoutMode={tryoutMode}
        progresTersimpan={progresTersimpan}
        onLanjutkan={handleLanjutkan}
        onMulaiBaru={handleMulaiBaruDariBanner}
      />
    );
  }
  if (fase === 'hasil') return <LayarHasil mode={mode} tryoutMode={tryoutMode} tryoutId={tryoutId} soalList={soalList} jawabanUser={jawabanUser} onUlang={handleUlang} onBack={onBack} />;

  const soal = soalList[indeks];
  if (!soal) return null;

  const jawabanSoalIni = jawabanUser.find(j => j.id === soal.id)?.jawaban;
  const progress       = ((indeks + 1) / soalList.length) * 100;
  const warnaMeter     = waktuSisa < 120 ? 'bg-red-500' : waktuSisa < 300 ? 'bg-yellow-400' : 'bg-purple-500';
  const info           = getLabelJenis(soal.jenis);

  // Divider "Mulai bagian ..." — hanya di mode campuran, dan hanya di soal
  // pertama tiap blok kategori (kategori beda dari soal sebelumnya).
  const mulaiKategoriBaru = mode === 'campuran' && (indeks === 0 || soalList[indeks - 1]?.jenis !== soal.jenis);
  const jumlahKategoriIni = soalList.filter(s => s.jenis === soal.jenis).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div className={`font-mono font-bold text-lg px-3 py-1 rounded-lg ${waktuSisa < 120 ? 'text-red-600 bg-red-50' : 'text-gray-700 bg-gray-100'}`}>
              ⏱ {formatWaktu(waktuSisa)}
            </div>
            <span className="text-sm text-gray-500 font-semibold">
              Soal <span className="text-purple-600">{indeks + 1}</span> / {soalList.length}
            </span>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${info.color}`}>{info.icon} {info.label}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all duration-300 ${warnaMeter}`} style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {mulaiKategoriBaru && (
          <div className={`flex items-center gap-2 rounded-xl px-4 py-2.5 mb-3 text-sm font-semibold border ${info.color}`}>
            <span>{info.icon}</span>
            <span>Mulai bagian: {info.label} ({jumlahKategoriIni} soal)</span>
          </div>
        )}
        <div className="bg-white rounded-2xl shadow p-6 mb-4">
          <SoalVerbal soal={soal} onJawab={handleJawab} jawaban={jawabanSoalIni} />
        </div>

        <div className="flex gap-3 mb-4">
          <button onClick={handlePrev} disabled={indeks === 0}
            className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed">
            ← Sebelumnya
          </button>
          {indeks < soalList.length - 1 ? (
            <button onClick={handleNext} className="flex-grow py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition">Selanjutnya →</button>
          ) : (
            <button onClick={handleSelesai} className="flex-grow py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition">Selesai ✓</button>
          )}
        </div>

        <div className="flex flex-wrap gap-1 justify-center">
          {soalList.map((s, i) => {
            const dijawab = jawabanUser.find(j => j.id === s.id)?.jawaban;
            const sudah   = dijawab !== null && dijawab !== undefined && dijawab !== '';
            return (
              <button key={s.id} onClick={() => setIndeks(i)}
                className={`w-8 h-8 text-xs rounded-lg font-semibold transition
                  ${i === indeks ? 'bg-purple-600 text-white'
                  : sudah ? 'bg-green-500 text-white'
                  : s.jenis === 'sinonim' ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  : s.jenis === 'antonim' ? 'bg-red-100 text-red-600 hover:bg-red-200'
                  : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'}`}>
                {i + 1}
              </button>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-400 mt-2">
          🟦 Aktif | 🟩 Dijawab{mode === 'campuran' ? ' | 🔵 Sinonim | 🔴 Antonim | 🟡 Kata Ganjil' : ''}
        </p>
      </div>
    </div>
  );
}
